import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL
const TIEMPO_POR_PREGUNTA = 8 // segundos

const COLOR_CATEGORIA = {
  vocabulario: '#3B82F6', gramatica: '#8B5CF6', pronunciacion: '#F59E0B', cultura: '#059669'
}
const NOMBRE_CATEGORIA = {
  vocabulario: 'Vocabulario', gramatica: 'Gramática', pronunciacion: 'Pronunciación', cultura: 'Cultura'
}

/* Juego de Verdadero o Falso: 10 afirmaciones al azar del banco
   (vocabulario, gramatica, pronunciacion, cultura), una a la vez
   contra un cronometro corto, con resumen y XP al final. Se puede
   jugar tantas veces como se quiera. */
export default function VerdaderoFalsoJuego({ usuario, onCambio }) {
  const [cargando,     setCargando]     = useState(true)
  const [afirmaciones, setAfirmaciones] = useState([])
  const [indice,       setIndice]       = useState(0)
  const [elegida,      setElegida]      = useState(null) // true | false | null
  const [validado,     setValidado]     = useState(false)
  const [aciertos,     setAciertos]     = useState(0)
  const [terminado,    setTerminado]    = useState(false)
  const [enviando,     setEnviando]     = useState(false)
  const [resultado,    setResultado]    = useState(null)
  const [error,        setError]        = useState(null)
  const [tiempo,       setTiempo]       = useState(TIEMPO_POR_PREGUNTA)

  const avanceRef = useRef(null)

  const nuevaPartida = useCallback(() => {
    setCargando(true)
    setError(null)
    setIndice(0)
    setElegida(null)
    setValidado(false)
    setAciertos(0)
    setTerminado(false)
    setResultado(null)
    setTiempo(TIEMPO_POR_PREGUNTA)
    axios.get(`${API}/juegos/verdadero-falso?cantidad=10`).then(res => {
      setAfirmaciones(res.data.datos || [])
    }).catch(err => {
      console.error('Error cargando verdadero/falso:', err)
      setError('No pudimos cargar las afirmaciones. Intenta de nuevo.')
    }).finally(() => setCargando(false))
  }, [])

  useEffect(() => { nuevaPartida() }, [nuevaPartida])

  const avanzar = useCallback(() => {
    setIndice(prevIndice => {
      const siguiente = prevIndice + 1
      return siguiente
    })
  }, [])

  function responder(respuesta) {
    if (validado || terminado || !afirmaciones[indice]) return
    setElegida(respuesta)
    setValidado(true)
    if (respuesta === afirmaciones[indice].es_verdadero) setAciertos(a => a + 1)
    if (avanceRef.current) clearTimeout(avanceRef.current)
    avanceRef.current = setTimeout(() => {
      if (indice < afirmaciones.length - 1) {
        avanzar()
        setElegida(null)
        setValidado(false)
        setTiempo(TIEMPO_POR_PREGUNTA)
      } else {
        setTerminado(true)
      }
    }, 1300)
  }

  // Cronometro por pregunta: si se agota el tiempo sin responder, cuenta como fallo.
  useEffect(() => {
    if (cargando || terminado || validado || !afirmaciones.length) return
    if (tiempo <= 0) {
      responder(null) // null nunca coincide con true/false, cuenta como fallo
      return
    }
    const t = setTimeout(() => setTiempo(s => s - 1), 1000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiempo, cargando, terminado, validado, afirmaciones.length])

  useEffect(() => () => { if (avanceRef.current) clearTimeout(avanceRef.current) }, [])

  async function reclamarXp() {
    if (enviando || !usuario?.id) return
    setEnviando(true)
    setError(null)
    try {
      const res = await axios.post(`${API}/juegos/verdadero-falso/completar`, {
        usuario_id: usuario.id, aciertos, total_afirmaciones: afirmaciones.length
      })
      setResultado(res.data)
      if (onCambio) onCambio()
    } catch (err) {
      console.error('Error guardando resultado de verdadero/falso:', err)
      setError('No pudimos guardar tu resultado. Intenta de nuevo.')
    } finally {
      setEnviando(false)
    }
  }

  if (cargando) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        Cargando afirmaciones...
      </div>
    )
  }

  if (!afirmaciones.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>⚖️</div>
        <p>Todavía no hay afirmaciones cargadas. ¡Vuelve pronto!</p>
      </div>
    )
  }

  return (
    <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>⚖️ Verdadero o Falso</h1>
        <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
          Responde rápido: ¿es cierto o falso sobre inglés?
        </p>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!terminado && (() => {
        const p = afirmaciones[indice]
        const color = COLOR_CATEGORIA[p.categoria] || 'var(--azul)'
        const acerto = elegida === p.es_verdadero
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--azul)' }}>Pregunta {indice + 1} de {afirmaciones.length}</span>
              <div style={{ flex: 1, height: 6, background: 'var(--gris-borde)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${(indice / afirmaciones.length) * 100}%`, height: '100%', background: 'var(--dorado)', borderRadius: 999, transition: 'width .3s ease' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B' }}>✓ {aciertos}</span>
            </div>

            <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: color, color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999 }}>
                    {NOMBRE_CATEGORIA[p.categoria] || p.categoria}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--gris-texto)' }}>{p.nivel}</span>
                </div>
                {!validado && (
                  <span style={{ fontSize: 13, fontWeight: 800, color: tiempo <= 3 ? '#EF4444' : 'var(--azul)' }}>
                    ⏱ {tiempo}s
                  </span>
                )}
              </div>

              <div style={{ height: 4, background: 'var(--gris-borde)', borderRadius: 999, overflow: 'hidden', marginBottom: 18 }}>
                <div style={{
                  width: `${(tiempo / TIEMPO_POR_PREGUNTA) * 100}%`, height: '100%',
                  background: tiempo <= 3 ? '#EF4444' : '#22C55E', borderRadius: 999, transition: 'width 1s linear'
                }} />
              </div>

              <p style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, lineHeight: 1.4 }}>{p.afirmacion}</p>

              <div style={{ display: 'flex', gap: 10 }}>
                {[{ v: true, texto: '✓ Verdadero' }, { v: false, texto: '✗ Falso' }].map(op => {
                  const esCorrecta = op.v === p.es_verdadero
                  const esElegida = elegida === op.v
                  let fondo = '#fff', borde = 'var(--gris-borde)'
                  if (validado && esCorrecta) { fondo = '#DCFCE7'; borde = '#22C55E' }
                  else if (validado && esElegida && !esCorrecta) { fondo = '#FEE2E2'; borde = '#EF4444' }
                  return (
                    <button key={String(op.v)} onClick={() => responder(op.v)} disabled={validado}
                      style={{
                        flex: 1, textAlign: 'center', padding: '14px', borderRadius: 12, border: '1.5px solid ' + borde,
                        background: fondo, color: '#1A1A2E', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 700,
                        cursor: validado ? 'default' : 'pointer'
                      }}>
                      {op.texto}
                    </button>
                  )
                })}
              </div>

              {validado && (
                <div style={{ background: elegida === null ? '#FEF3C7' : (acerto ? '#F8FAFF' : '#FEF3C7'), borderRadius: 10, padding: 12, marginTop: 16 }}>
                  <p style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>
                    {elegida === null ? '⏱ Se acabó el tiempo. ' : ''}💡 {p.explicacion}
                  </p>
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {terminado && (
        <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 26, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>{aciertos >= afirmaciones.length * 0.7 ? '🎉' : '💪'}</div>
          <h2 style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>¡Partida terminada!</h2>
          <p style={{ fontSize: 14, color: 'var(--gris-texto)', marginBottom: 20 }}>
            Acertaste {aciertos} de {afirmaciones.length} afirmaciones.
          </p>

          {resultado ? (
            <div>
              <div style={{ background: 'linear-gradient(135deg,#065F46,#059669)', color: '#fff', borderRadius: 14, padding: 18, marginBottom: 18 }}>
                <div style={{ fontSize: 20, fontWeight: 800 }}>+{resultado.xp_ganado} XP ganados</div>
              </div>
              <button onClick={nuevaPartida}
                style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--azul)', color: '#fff',
                  fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                🔁 Jugar de nuevo
              </button>
            </div>
          ) : (
            <button onClick={reclamarXp} disabled={enviando}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', fontFamily: 'inherit',
                fontSize: 15, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer',
                background: enviando ? '#CBD5E1' : 'var(--dorado)', color: '#fff' }}>
              {enviando ? 'Guardando...' : `Reclamar +${aciertos * 2} XP`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
